import { FC, MutableRefObject, useCallback, useMemo } from 'react';
import moment from 'moment';

import { getTimeSlots, mapBookingItemsToSlots } from 'utils/booking';
import Slot from '../timeslotWidget/slot';

import { AVAILABILITY_STATUSES } from '../../constants/fieldData';
import { TIMESLOT_STEP, MINUTES_PER_INCREMENT } from '../../constants/timeslot';

interface SlotType {
  startTime: string;
  endTime: string;
}

interface Props {
  date: string;
  startTime: string;
  endTime: string;
  loading: boolean;
  availability: any;
  bookingItems: any;
  horizontal: boolean;
  scale: number;
  scrollRef: MutableRefObject<{}>;
  times: any;
  value?: SlotType[];
  onChange?: (value: any[]) => void;
  setTouched?: (value: boolean) => void;
  disabled?: boolean;
}

const Slots: FC<Props> = ({
  date,
  startTime,
  endTime,
  loading = false,
  availability,
  bookingItems,
  horizontal = true,
  scale = 2,
  scrollRef,
  times,
  value,
  onChange: setValue = () => {},
  setTouched = () => {},
  disabled = undefined,
}) => {
  const getFormattedSlot = useCallback(
    (startTime, endTime, rest) => {
      const startIndex = times.indexOf(startTime);

      const startTimestamp = moment(startTime, 'HH:mm');
      const endTimestamp = moment(endTime, 'HH:mm');

      const timeDifferenceInHours = moment
        .duration(endTimestamp.diff(startTimestamp))
        .asHours();

      return {
        startTime,
        endTime,
        edge: startIndex * (25 / 2),
        length: timeDifferenceInHours * 25 * scale,
        ...rest,
      };
    },
    [times, scale]
  );

  const slots = useMemo(
    () => [
      ...(loading ? [] : getTimeSlots(availability, value)),
      ...mapBookingItemsToSlots(bookingItems),
    ],
    [bookingItems, loading, availability, value]
  );

  const formattedSlots = slots.map(({ startTime, endTime, ...rest }) =>
    getFormattedSlot(startTime, endTime, rest)
  );

  const setTimesForDate = useCallback(
    (timeString: string) => {
      const time = moment(timeString, 'HH:mm');
      return moment(date).hours(time.hours()).minutes(time.minutes());
    },
    [date]
  );

  const startTimeForDate = useMemo(() => setTimesForDate(startTime), [
    setTimesForDate,
    startTime,
  ]);

  const endTimeForDate = useMemo(() => setTimesForDate(endTime), [
    setTimesForDate,
    endTime,
  ]);

  const handleSlotResize = (event, dir, ref, delta, { x, y }) => {
    const position = horizontal ? x : y;
    const startOffset = Math.round(
      (position / TIMESLOT_STEP / scale) * MINUTES_PER_INCREMENT
    );
    const length = horizontal ? ref.style.width : ref.style.height;
    const slotStart = startTimeForDate
      .clone()
      .add(startOffset - (startOffset % MINUTES_PER_INCREMENT), 'minutes');

    const slotEnd = slotStart
      .clone()
      .add(
        (parseInt(length, 10) / TIMESLOT_STEP / scale) * MINUTES_PER_INCREMENT,
        'minutes'
      );

    if (
      slotStart.isBefore(slotEnd) &&
      slotStart.isSameOrAfter(moment(startTimeForDate, 'HH:mm')) &&
      slotEnd.isSameOrBefore(moment(endTimeForDate, 'HH:mm'))
    ) {
      setTouched(true);
      setValue([
        {
          startTime: slotStart.format('HH:mm'),
          endTime: slotEnd.format('HH:mm'),
        },
      ]);
    }

    scrollRef.current.unlock();
  };

  return (
    <div
      className="timeslot-widget timeslot-widget__horizontal"
      data-testid="timeslotContainer"
    >
      {formattedSlots.map(slot => (
        <Slot
          key={`${slot.startTime}-${slot.endTime}`}
          availabilityStatus={
            disabled
              ? AVAILABILITY_STATUSES.PROVISIONAL
              : AVAILABILITY_STATUSES[slot.available]
          }
          length={slot.length * 2}
          edge={slot.edge * 2}
          resizable={!disabled && slot.editable}
          handleResize={handleSlotResize}
          onResizeStart={scrollRef.current.lock}
          onResizeStop={scrollRef.current.unlock}
          horizontal={horizontal}
          scale={scale}
        />
      ))}
    </div>
  );
};

export default Slots;
